// src/components/forms/mapping-editor/SimpleListEditor.tsx (Create this file)
import React, { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

interface SimpleListEditorProps<T> {
  items: T[] | undefined; // The array of items to edit
  updateItems: (newItems: T[]) => void; // Callback to update the parent state's array
  renderItem: (
      item: T,
      index: number,
      // Callback for the item renderer to signal a change to THIS item
      onChange: (index: number, updatedItem: T) => void
  ) => React.ReactNode; // Function to render the inputs for a single item
  createDefaultItem: () => T; // Function to create a new blank item
  title: string; // Title for this section
  itemTitle?: string; // Optional title for each item (e.g., "Default Rule")
  addButtonText?: string; // Text for the add button
}

// Use generic type T for the item type
export function SimpleListEditor<T>({
  items = [], // Default to empty array if undefined
  updateItems,
  renderItem,
  createDefaultItem,
  title,
  itemTitle = "Item",
  addButtonText = "Add Item",
}: SimpleListEditorProps<T>) {

  const handleItemChange = useCallback((index: number, updatedItem: T) => {
    const newItems = [...items]; // Create a mutable copy
    if (index >= 0 && index < newItems.length) {
      newItems[index] = updatedItem; // Replace item at index
      updateItems(newItems); // Update parent state
    }
  }, [items, updateItems]);

  const handleAddItem = useCallback(() => {
    const newItem = createDefaultItem();
    updateItems([...items, newItem]); // Add new default item to parent state
  }, [items, updateItems, createDefaultItem]);

  const handleRemoveItem = useCallback((index: number) => {
    // Filter out the item at the specified index
    const newItems = items.filter((_, i) => i !== index);
    updateItems(newItems); // Update parent state
  }, [items, updateItems]);

  return (
    <div className="p-4 border rounded bg-white shadow-sm mb-6">
      <h3 className="text-lg font-semibold mb-1 text-gray-700">{title}</h3>
      <p className="text-xs text-gray-500 mb-3">Manage the list of {itemTitle.toLowerCase()}s below.</p>
      {items && items.length > 0 ? (
        <ul className="space-y-3">
          {items.map((item, index) => (
            <li key={index} className="border rounded p-3 bg-gray-50 relative">
              <div className="absolute top-2 right-2">
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-400"
                  title={`Remove ${itemTitle} ${index + 1}`}
                  aria-label={`Remove ${itemTitle} ${index + 1}`}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
              {/* Render the specific inputs for this item type */}
              {renderItem(item, index, handleItemChange)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 italic">No {itemTitle.toLowerCase()}s added yet.</p>
      )}
      <button
        type="button"
        onClick={handleAddItem}
        className="mt-4 inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <FontAwesomeIcon icon={faPlus} className="-ml-1 mr-2 h-4 w-4" />
        {addButtonText}
      </button>
    </div>
  );
}